import { ApolloProvider as Provider } from '@apollo/client'
import { createApolloClient } from '@polf/shared/apollo'
import { possibleTypesData } from '@polf/shared/codegen'
import { type ReactNode, useMemo } from 'react'

type Props = { children: ReactNode }

const ApolloProvider = ({ children }: Props) => {
  const client = useMemo(
    () =>
      createApolloClient({
        authMode: 'jwt',
        uri: '/graphql',
        onUnauthorized: () => {
          if (window.location.pathname !== '/login') {
            window.location.assign('/login')
          }
        },
        // Required for any GraphQL union dispatch (e.g. PolfEventPlayer.payment
        // → VippsPayment | PolfEventPlayerOtherPayment). Without this Apollo
        // silently drops `... on X` fragments on cache read.
        possibleTypes: possibleTypesData.possibleTypes,
        // Flight members / team players come back as full arrays on refetch.
        // Apollo's default deep-merge-by-id corrupts the list when count changes.
        cacheTypePolicies: {
          PolfEventFlight: {
            fields: {
              players: {
                merge: (_existing: unknown, incoming: unknown) => incoming,
              },
              teams: {
                merge: (_existing: unknown, incoming: unknown) => incoming,
              },
            },
          },
          PolfEventTeam: {
            fields: {
              players: {
                merge: (_existing: unknown, incoming: unknown) => incoming,
              },
            },
          },
        } as never,
      }),
    []
  )

  return <Provider client={client}>{children}</Provider>
}

export default ApolloProvider
